import cx from 'classnames'
import { PropsWithChildren, useState, useRef, useEffect } from 'react'
import { Modal, useModals } from 'almanac-ui/Modal'
import { observer } from 'mobx-react-lite'

import Styles from './CreditsBalanceModal.module.scss'
import { Credits } from 'blaze-ui/icons/20/Credits'
import { Heading } from 'blaze-ui/Heading/Heading'
import { useCreditUsage } from 'hooks/useCreditUsage'
import { Button } from 'blaze-ui/Button'
import { useRootStore } from 'stores/RootStore'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { PurchaseContentCreditsModal } from 'components/Settings/TieredBilling/components/AutopilotDetails/components/PurchaseContentCreditsModal/PurchaseContentCreditsModal'
import { ReferralModalV2 } from 'components/Settings/ReferralModalV2/ReferralModalV2'
import { ChevronDown2, LinkExternal } from 'blaze-ui/icons'
import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import dayjs from 'dayjs'
import { nameMap } from 'components/Settings/Workspace/AutopilotPlans/components/Plans/components/PlanDetails/PlanDetails'
import { AutopilotPlansModal } from 'components/Settings/Workspace/AutopilotPlans/AutopilotPlansModal'
import { pluralize } from 'utils'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

const EducationRow = ({
  text,
  children,
  isOpen,
  onToggle,
}: PropsWithChildren<{
  text: string
  isOpen: boolean
  onToggle: () => void
}>) => {
  return (
    <div className={Styles.educationRow}>
      <div className={Styles.textAndChevron} onClick={onToggle}>
        <span className={cx(Styles.text, Styles.grow)}>{text}</span>
        <div className={cx(Styles.chevronWrapper, { [Styles.rotated]: isOpen })}>
          <ChevronDown2 />
        </div>
      </div>
      <div className={cx(Styles.accordionContent, { [Styles.open]: isOpen })}>
        <div className={Styles.accordionInner}>{children}</div>
      </div>
    </div>
  )
}

export const CreditsBalanceModal = observer(() => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const { openModal, closeModal } = useModals()
  const { creditsRemaining, baseCreditsAvailable, usageCosts, endDate } = useCreditUsage()
  const {
    tierUsageStore: { currentSubscription, autopilotAddOns },
  } = useRootStore()
  const [openAccordion, setOpenAccordion] = useState<string | null>(null)
  const transitionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isOnPioneer = currentSubscription?.plan === PaymentPlanKey.VirtualMarketer
  const autopilotContentCreditsAddon = autopilotAddOns.find(
    (addon) => addon.key === PaymentPlanKey.BlazeAutopilotContentCredits,
  )
  const autopilotContentCreditsPrice = autopilotContentCreditsAddon?.prices[0]

  const openPurchaseCreditsModal = () => {
    if (!isOnPioneer && currentSubscription?.status === 'trialing') {
      openModal(AutopilotPlansModal)
      return
    }

    if (!autopilotContentCreditsPrice) return

    closeModal()
    openModal(PurchaseContentCreditsModal, { price: autopilotContentCreditsPrice })
  }

  const openReferralModal = () => {
    openModal(ReferralModalV2, { onAction: closeModal })
  }

  const toggleAccordion = (accordionKey: string) => {
    // Clear any existing timeout
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current)
      transitionTimeoutRef.current = null
    }

    // If clicking the same accordion, just toggle it
    if (openAccordion === accordionKey) {
      setOpenAccordion(null)
      return
    }

    // If no accordion is open, open the clicked one immediately
    if (!openAccordion) {
      setOpenAccordion(accordionKey)
      return
    }

    // If switching between accordions, close current one first
    setOpenAccordion(null)

    // Wait for close animation to complete, then open new one
    transitionTimeoutRef.current = setTimeout(() => {
      setOpenAccordion(accordionKey)
      transitionTimeoutRef.current = null
    }, 300) // Match the CSS transition duration
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current)
      }
    }
  }, [])

  const pluralizedCredits = (cost: number) => {
    return `${cost} ${pluralize('credit', cost)}`
  }

  let ctaButtonLabel = currentSubscription?.status === 'active' ? 'Buy More Credits' : 'Upgrade Plan'

  return (
    <Modal.Root>
      <Modal.Header title="Your Account Balance" />
      <Modal.Content withoutFooter={isSystemRedesignEnabled}>
        <div className={Styles.content}>
          <div className={Styles.top}>
            <div className={Styles.cta}>
              <div className={Styles.creditsCount}>
                <Credits color="var(--purple-50)" size={47} />
                <Heading level={1}>{creditsRemaining} credits</Heading>
              </div>
              <div className={Styles.planInformationAndButton}>
                {!isOnPioneer && currentSubscription?.status === 'active' && (
                  <div className={Styles.planInformation}>
                    <span className={cx(Styles.text, Styles.messageText)}>
                      Your {currentSubscription?.plan ? nameMap[currentSubscription?.plan] : ''} plan provides{' '}
                      <strong>{baseCreditsAvailable}</strong> credits that reset on{' '}
                      {dayjs(endDate).add(1, 'day').format('MMM D')}, and purchased credits expire after 30 days.
                    </span>
                  </div>
                )}
                {!isOnPioneer && currentSubscription?.status === 'trialing' && (
                  <div className={Styles.planInformation}>
                    <span className={cx(Styles.text, Styles.messageText)}>
                      Your {currentSubscription?.plan ? nameMap[currentSubscription?.plan] : ''} trial includes{' '}
                      <strong>{baseCreditsAvailable}</strong> credits on signup. Upgrade for fewer limits!
                    </span>
                  </div>
                )}
                {isOnPioneer && (
                  <span className={cx(Styles.text, Styles.messageText)}>
                    Your Pioneer plan requires credits to be purchased to access Stylized Images and AI Videos.
                  </span>
                )}
                <Button onPress={openPurchaseCreditsModal}>{ctaButtonLabel}</Button>
              </div>
            </div>
            <span className={Styles.refer}>
              or{' '}
              <span className={Styles.underline} onClick={openReferralModal}>
                refer a friend
              </span>{' '}
              to get more credits
            </span>
          </div>
          <div className={Styles.bottom}>
            <div className={Styles.educationContainer}>
              <EducationRow
                text="What are credits?"
                isOpen={openAccordion === 'what-are-credits'}
                onToggle={() => toggleAccordion('what-are-credits')}
              >
                <div className={Styles.whatAreCreditsContainer}>
                  <span className={Styles.whatAreCredits}>
                    Blaze uses usage-based pricing in the form of credits to allow users to create various forms of
                    content in your workspace.
                  </span>
                  <span className={Styles.whatAreCredits}>
                    This allows Blaze to integrate with the best LLM and AI tools and gives you control over the quality
                    and quantity of content you want in your marketing.
                  </span>
                </div>
              </EducationRow>
              <EducationRow
                text="What uses credits?"
                isOpen={openAccordion === 'what-uses-credits'}
                onToggle={() => toggleAccordion('what-uses-credits')}
              >
                <div className={Styles.whatUsesCreditsContainer}>
                  {!isOnPioneer && (
                    <span className={Styles.creditCost}>
                      {pluralizedCredits(usageCosts?.content_credits_cost || 0)} per Brand Kit and Stock Content
                    </span>
                  )}
                  <span className={Styles.creditCost}>
                    {pluralizedCredits(usageCosts?.visual_styles_credits_cost || 0)} per Stylized Image
                  </span>
                  <span className={Styles.creditCost}>
                    {pluralizedCredits(usageCosts?.video_credits_cost || 0)} per AI Video
                  </span>
                </div>
              </EducationRow>
              <EducationRow
                text="How can I earn more credits?"
                isOpen={openAccordion === 'how-to-earn-credits'}
                onToggle={() => toggleAccordion('how-to-earn-credits')}
              >
                <div className={Styles.learnMoreTextContainer}>
                  <span className={Styles.learnMoreText}>
                    You can earn 200 credits for every friend or peer that you refer to Blaze using your referral link.{' '}
                    <a
                      href="https://help.blaze.ai/en/articles/13017540-blaze-credits"
                      target="_blank"
                      rel="noreferrer"
                      className={Styles.underline}
                    >
                      Learn more about Blaze's referral program
                    </a>
                    .
                  </span>
                </div>
              </EducationRow>
            </div>
            <div className={Styles.bottomButtonContainer}>
              <ButtonLink
                to="https://help.blaze.ai/en/articles/13017540-blaze-credits"
                target="_blank"
                variant="editor"
                endIcon={LinkExternal}
              >
                Learn More About Credits
              </ButtonLink>
            </div>
          </div>
        </div>
      </Modal.Content>
    </Modal.Root>
  )
})
